@if (isBanner() && mediaUrl() && !hideImage()) {
  <div class="cmp-card__media" [style.background-image]="mediaUrl()" [style.background-position]="imagePosition()"></div>
  <div class="cmp-card__scrim" aria-hidden="true"></div>
}

<div class="cmp-card__body">
  @switch (variant()) {
    @case ('content') {
      @if (isBanner()) {
        <div class="cc cc--banner">
          <div class="cc__img-top">
            @if (icon()) {
              <div class="cc__media-icon" [innerHTML]="iconSvg(icon(), 48)" aria-hidden="true"></div>
            }
            @if (category()) { <span class="cc__chip cc__chip--glass">{{ category() }}</span> }
          </div>
          <div class="cc__img-foot">
            @if (title() || subtitle() || headline() || description() || headlineDescription() || metrics().length) {
              <div class="cc__banner-text">
                @if (title() || subtitle()) {
                  <div class="cc__heading">
                    @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'cc__title', text: title() }" /> }
                    @if (subtitle()) { <p class="cc__subtitle">{{ subtitle() }}</p> }
                  </div>
                }
                @if (description()) { <p class="cc__desc">{{ description() }}</p> }
                @if (headline()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'cc__headline', text: headline() }" /> }
                @if (headlineDescription()) { <p class="cc__headline-desc" [innerHTML]="headlineDescription()"></p> }
                @if (metrics().length) {
                  <div class="cc__metrics">
                    @for (m of metrics(); track $index) {
                      <compsych-card variant="metric" [value]="m.value" [label]="m.label ?? ''" [current]="m.current ?? false" [darkBackground]="true" metricSize="small" />
                    }
                  </div>
                }
              </div>
            }
            @if (actionLabel()) {
              <compsych-button variant="elevated" [label]="actionLabel()" [leadingIcon]="actionLeadingIcon()" [trailingIcon]="actionTrailingIcon()" [disabled]="actionDisabled()" (click)="actionClick.emit(); $event.stopPropagation()" />
            }
          </div>
        </div>
      } @else if (appearance() === 'gradient') {
        <div class="cc">
          <div class="cc__top" [class.cc__top--label]="categoryStyle() === 'label'">
            @if (icon()) {
              <div class="cc__icon" [class.cc__icon--neutral]="iconStyle() === 'neutral'" [innerHTML]="iconSvg(icon(), 24)" aria-hidden="true"></div>
            }
            @if (categoryStyle() === 'label' && category()) {
              <span class="cc__category-label">{{ category() }}</span>
            }
            <div class="cc__top-end">
              @if (categoryStyle() === 'chip' && category()) { <span class="cc__chip">{{ category() }}</span> }
              @if (showArrow()) {
                <span class="cc__arrow" [innerHTML]="iconSvg('arrow-up-right', 24)" aria-hidden="true"></span>
              }
            </div>
          </div>
          <div class="cc__text">
            @if (title() || subtitle()) {
              <div class="cc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'cc__title', text: title() }" /> }
                @if (subtitle()) { <p class="cc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="cc__desc">{{ description() }}</p> }
          </div>
          @if (actionLabel()) {
            <compsych-button variant="outlined" [label]="actionLabel()" [leadingIcon]="actionLeadingIcon()" [trailingIcon]="actionTrailingIcon()" [disabled]="actionDisabled()" />
          }
        </div>
      } @else {
        <div class="cc cc--with-icon" [class.cc--filled]="appearance() === 'filled'" [class.cc--inline-icon]="inlineIcon()">
          @if (inlineIcon()) {
            @if (icon()) {
              <span class="cc__banner-icon" [innerHTML]="iconSvg(icon(), 32, 1.5)" aria-hidden="true"></span>
            }
          } @else {
            <div class="cc__banner">
              @if (icon()) {
                <span class="cc__banner-icon" [innerHTML]="iconSvg(icon(), 48, 1.5)" aria-hidden="true"></span>
              }
            </div>
          }
          <div class="cc__container">
            <div class="cc__title-group">
              @if (category()) { <p class="cc__category-eyebrow">{{ category() }}</p> }
              @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'cc__title', text: title() }" /> }
            </div>
            @if (description()) { <p class="cc__desc">{{ description() }}</p> }
            @if (headline()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'cc__headline', text: headline() }" /> }
          </div>
        </div>
      }
    }

    @case ('action') {
      @if (orientation() === 'vertical') {
        <div class="ac ac--vertical" [class.ac--no-desc]="!description()">
          @if (icon()) {
            <div class="ac__badge" [innerHTML]="iconSvg(icon(), 24, 1.5)" aria-hidden="true"></div>
          }
          <div class="ac__text">
            <ng-container [ngTemplateOutlet]="labelTpl" />
            @if (description()) { <span class="ac__desc">{{ description() }}</span> }
          </div>
        </div>
      } @else {
        <div class="ac" [class.ac--no-desc]="!description()">
          @if (icon()) {
            <div class="ac__icon" [innerHTML]="iconSvg(icon(), 32)" aria-hidden="true"></div>
          }
          <div class="ac__text">
            <ng-container [ngTemplateOutlet]="labelTpl" />
            @if (description()) { <span class="ac__desc">{{ description() }}</span> }
          </div>
        </div>
      }
    }

    @case ('metric') {
      <div class="mc" [class.mc--current]="current()">
        <span class="mc__value">{{ value() }}</span>
        @if (label()) { <span class="mc__label">{{ label() }}</span> }
      </div>
    }

    @case ('basic') {
      @if (size() === 'xs') {
        <!-- xs base card (Figma 243:3264): compact horizontal row — icon · title · arrow. -->
        <div class="bc bc--row">
          @if (icon()) {
            <div class="bc__icon" [innerHTML]="iconSvg(icon(), 24, 1.5)" aria-hidden="true"></div>
          }
          <div class="bc__row-text">
            @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'bc__title', text: title() }" /> }
          </div>
          @if (showArrow()) {
            <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', 20)" aria-hidden="true"></span>
          }
        </div>
      } @else if (size() === 'lg') {
        <!-- lg base card (Figma 243:3205): text + optional chip, button bottom-right. -->
        <div class="bc bc--promo" [class.bc--banner]="isBanner()">
          <div class="bc__text">
            @if (label()) { <p class="bc__eyebrow">{{ label() }}</p> }
            @if (title() || subtitle()) {
              <div class="bc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'bc__title', text: title() }" /> }
                @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="bc__desc">{{ description() }}</p> }
            @if (category()) { <span class="bc__chip">{{ category() }}</span> }
          </div>
          @if (actionLabel()) { <compsych-button [variant]="appearance() === 'filled' ? 'elevated' : 'outlined'" [label]="actionLabel()" [disabled]="actionDisabled()" /> }
        </div>
      } @else if (isBanner()) {
        <!-- md banner base card (Figma 59442:12042): text over the image, arrow bottom-right. -->
        <div class="bc bc--banner">
          <div class="bc__text">
            @if (title() || subtitle()) {
              <div class="bc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'bc__title', text: title() }" /> }
                @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="bc__desc">{{ description() }}</p> }
          </div>
          @if (showArrow()) {
            <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', basicArrowSize())" aria-hidden="true"></span>
          }
        </div>
      } @else {
        <!-- md / sm base card (Figma 243:3174, 243:3179, 947:8067):
             icon + arrow row, then eyebrow / title / subtitle / description / button. -->
        <div class="bc">
          <div class="bc__top">
            @if (icon()) {
              @if (appearance() === 'gradient') {
                <div class="bc__icon bc__icon--badge" [innerHTML]="iconSvg(icon(), 32)" aria-hidden="true"></div>
              } @else {
                <div class="bc__icon" [innerHTML]="iconSvg(icon(), basicIconSize(), 1.5)" aria-hidden="true"></div>
              }
            }
            <div class="bc__top-end">
              @if (category()) { <span class="bc__chip">{{ category() }}</span> }
              @if (showArrow()) {
                <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', basicArrowSize())" aria-hidden="true"></span>
              }
            </div>
          </div>
          <div class="bc__body">
            <div class="bc__text">
              @if (label()) { <p class="bc__eyebrow">{{ label() }}</p> }
              @if (title() || subtitle()) {
                <div class="bc__heading">
                  @if (title()) { <ng-container [ngTemplateOutlet]="headingTpl" [ngTemplateOutletContext]="{ $implicit: 'bc__title', text: title() }" /> }
                  @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
                </div>
              }
              @if (description()) { <p class="bc__desc">{{ description() }}</p> }
            </div>
            @if (actionLabel()) { <compsych-button [variant]="appearance() === 'filled' ? 'elevated' : 'outlined'" [label]="actionLabel()" [disabled]="actionDisabled()" /> }
          </div>
        </div>
      }
    }

    @default {
      <ng-content />
    }
  }
</div>

<!-- Heading fragment: renders <h2>–<h6> when headingLevel is set, else <p>.
     Pass $implicit = CSS class string, text = display text.
     Used by all title/headline slots across content and basic variants. -->
<ng-template #headingTpl let-cls let-text="text">
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 [class]="cls">{{ text }}</h2> }
    @case (3) { <h3 [class]="cls">{{ text }}</h3> }
    @case (4) { <h4 [class]="cls">{{ text }}</h4> }
    @case (5) { <h5 [class]="cls">{{ text }}</h5> }
    @case (6) { <h6 [class]="cls">{{ text }}</h6> }
    @default { <p [class]="cls">{{ text }}</p> }
  }
</ng-template>

<!-- Action-card label: same heading-level logic but defaults to <span> (inline) not <p>. -->
<ng-template #labelTpl>
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 class="ac__label">{{ label() }}</h2> }
    @case (3) { <h3 class="ac__label">{{ label() }}</h3> }
    @case (4) { <h4 class="ac__label">{{ label() }}</h4> }
    @case (5) { <h5 class="ac__label">{{ label() }}</h5> }
    @case (6) { <h6 class="ac__label">{{ label() }}</h6> }
    @default { <span class="ac__label">{{ label() }}</span> }
  }
</ng-template>
